import { z } from 'zod'

import { locales } from '@/lib/i18n/config'
import { INSTITUTION_KINDS } from '@/lib/org/institution-noun'
import { ALUMNI_KINDS } from '@/lib/org/alumni-noun'
import { SHORT_NAME_MAX } from '@/lib/org/short-name'
import { isReservedSlug, normalizeSlug, SLUG_REGEX } from '@/lib/org/reserved-slugs'
import { isValidTimezone } from '@/lib/utils/iana-zones'
import { SUGGESTED_GROUP_KINDS } from '@/lib/institution-lookup/types'

/** The longest institution name the wizard saves; longer is refused, never cut (ALU-357). */
export const INSTITUTION_NAME_MAX = 160

/**
 * What the new-institution wizard's "Crear" sends (ALU-157). Parsed on the
 * server before anything is written; the wizard parses the same schema to
 * show errors before sending.
 */

export const NEW_INSTITUTION_PURPOSES = ['demo', 'customer'] as const
export type NewInstitutionPurpose = (typeof NEW_INSTITUTION_PURPOSES)[number]

/** `colegio.edu.ar`: lower case, dots, no scheme, no `www.` (organizations.website_domain). */
export const WEBSITE_DOMAIN_REGEX =
  /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/

const emptyToNull = (value: unknown) =>
  typeof value === 'string' && value.trim() === '' ? null : value

const optionalText = (max: number) =>
  z.preprocess(emptyToNull, z.string().trim().max(max).nullable())

/** A suggestion's value as the wizard received it, for "which fields were edited". */
const suggestedValue = z.union([z.string().max(200), z.number(), z.null()])

export const newInstitutionSchema = z
  .object({
    purpose: z.enum(NEW_INSTITUTION_PURPOSES),
    sourceUrl: z.preprocess(emptyToNull, z.string().url().max(2048).nullable()),
    websiteDomain: z.preprocess(
      emptyToNull,
      z
        .string()
        .trim()
        .toLowerCase()
        .transform((d) => d.replace(/^www\./, ''))
        .pipe(z.string().max(253).regex(WEBSITE_DOMAIN_REGEX))
        .nullable()
    ),
    name: z.string().trim().min(2).max(INSTITUTION_NAME_MAX),
    shortName: optionalText(SHORT_NAME_MAX),
    slug: z
      .string()
      .transform((v) => normalizeSlug(v))
      .pipe(
        z
          .string()
          .min(3)
          .max(40)
          .regex(SLUG_REGEX)
          .refine((v) => !isReservedSlug(v), 'reserved')
      ),
    /** The chosen logo's preview, a PNG data URL the lookup made on our server. */
    logoPreview: z.preprocess(
      emptyToNull,
      z
        .string()
        .max(1_500_000)
        .regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/)
        .nullable()
    ),
    color: z.preprocess(
      emptyToNull,
      z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/)
        .transform((v) => v.toLowerCase())
        .nullable()
    ),
    language: z.enum(locales),
    country: z.preprocess(
      emptyToNull,
      z
        .string()
        .regex(/^[A-Z]{2}$/)
        .nullable()
    ),
    city: optionalText(100),
    timezone: z.preprocess(
      emptyToNull,
      z.string().max(64).refine(isValidTimezone, 'timezone').nullable()
    ),
    institutionType: z.preprocess(emptyToNull, z.enum(INSTITUTION_KINDS).nullable()),
    alumniWord: z.preprocess(emptyToNull, z.enum(ALUMNI_KINDS).nullable()),
    classYearTerm: optionalText(60),
    foundingYear: z.preprocess(
      (v) => (v === '' || v === undefined ? null : v),
      z.coerce.number().int().min(1500).max(new Date().getUTCFullYear()).nullable()
    ),
    groups: z
      .array(
        z.object({
          name: z.string().trim().min(2).max(80),
          kind: z.enum(SUGGESTED_GROUP_KINDS),
        })
      )
      .max(12)
      .default([]),
    adminEmail: z.preprocess(
      emptyToNull,
      z.string().trim().toLowerCase().email().max(254).nullable()
    ),
    /** The lookup's suggestions as received (values only), to record what was edited. */
    suggested: z.record(z.string().max(40), suggestedValue).default({}),
    /** The lookup's full suggestions (confidence, source), stored for phase 2. */
    lookup: z
      .object({
        suggestions: z.record(z.string(), z.unknown()),
        notes: z.array(z.string().max(40)).max(20),
        aiModel: z.string().max(120).nullable(),
      })
      .nullable()
      .default(null)
      .refine((v) => v === null || JSON.stringify(v).length <= 64_000, 'lookup too large'),
  })
  // A demo sends no email at creation: its admin is invited later, by hand.
  .refine((v) => v.purpose === 'customer' || v.adminEmail === null, {
    path: ['adminEmail'],
    message: 'demo_no_invite',
  })

export type NewInstitutionInput = z.input<typeof newInstitutionSchema>
export type NewInstitution = z.output<typeof newInstitutionSchema>
